"use client";
import type {
    FocusEvent,
    KeyboardEvent as ReactKeyboardEvent,
    MouseEvent as ReactMouseEvent,
} from "react";
import { memo, useCallback, useEffect, useId, useRef } from "react";
import { ContentLink } from "@components/cms/link/ContentLink";
import {
    animatedContainerStyles,
    bottomContainerLinkStyles,
    innerNavLeftGridStyles,
    innerNavRightStyles,
    menuContainerStyles,
    NAV_HOVER_DELAY_MS,
    navItemContainerStyles,
    navItemHoverWrapperStyles,
    navItemStyles,
    outerNavLeftStyles,
    salesNavItemContainerStyles,
    salesNavItemStyles,
} from "@components/layouts/navigation/styles";
import type { NavigationColumn, NavigationItem } from "@lib/contentful/mappers/mapNavigation";
import { useFocus } from "@hooks/useFocus";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Grid, GridItem } from "@ecco/ui/grid";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { VisuallyHidden } from "@ecco/ui/visually-hidden";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { CategoryTile } from "../components/CategoryTile";
import { FallbackImageNavigation } from "../components/FallbackImageNavigation";
import { ThumbnailGrid } from "../components/ThumbnailGrid";
import { DesktopSubNavItem } from "./DesktopSubNavItem";
import { Portal } from "@ecco/ui/portal";
import { DESKTOP_OPEN_SEARCH_ICON, MENU_DROPDOWN } from "@e2e/helpers/navigation/constants";

export interface DesktopNavItemProps {
    navigationItem: NavigationItem;
    isOpen: boolean;
    setHeightNav: (height: number) => void;
    heightNav: number;
    onOpen: () => void;
    onClose: () => void;
    isHoveringNavBar?: boolean;
    shouldRenderDropdown?: boolean;
    onItemClose?: () => void;
    onItemHover?: () => void;
    onItemEnter?: () => void;
    isActive?: boolean;
}

const PRIMARY_NAV_LINK_SELECTOR = '#navigation [data-desktop-nav-primary="true"]';

const queryPanelFocusables = (root: HTMLElement | null): HTMLElement[] => {
    if (!root) {
        return [];
    }

    const all = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    );

    return Array.from(all).filter((el) => {
        if (el.hasAttribute("disabled")) {
            return false;
        }

        const s = window.getComputedStyle(el);
        return s.display !== "none" && s.visibility !== "hidden";
    });
};

const getPrimaryNavTriggers = (): HTMLElement[] => {
    return Array.from(document.querySelectorAll<HTMLElement>(PRIMARY_NAV_LINK_SELECTOR));
};

const getPrimaryNavTrigger = (testId?: string): HTMLElement | undefined =>
    testId
        ? getPrimaryNavTriggers().find((trigger) => trigger.dataset.testid === testId)
        : undefined;

const isTabFocusable = (el: HTMLElement | null): el is HTMLElement => {
    if (!el) {
        return false;
    }
    if (el.hasAttribute("disabled")) {
        return false;
    }
    if (el.getAttribute("tabindex") === "-1") {
        return false;
    }
    const s = window.getComputedStyle(el);
    return s.display !== "none" && s.visibility !== "hidden";
};

const focusDesktopSearchTrigger = (): boolean => {
    const searchBtn = document.querySelector<HTMLElement>(
        `[data-testid="${DESKTOP_OPEN_SEARCH_ICON}"]`
    );
    if (!isTabFocusable(searchBtn)) {
        return false;
    }
    searchBtn.focus({ preventScroll: true });
    return true;
};

const filterByColumns = (columns: NavigationColumn[], columnNumber: number) => {
    return columns
        ?.map(
            (linkCollection) =>
                linkCollection.position === columnNumber && (
                    <DesktopSubNavItem
                        key={linkCollection.id}
                        title={linkCollection.title}
                        titleLink={linkCollection.titleLink}
                        links={linkCollection.links}
                    />
                )
        )
        .filter(Boolean);
};

const checkFourColumns = (columns: NavigationColumn[]) =>
    columns.some((item) => item.position === 4);

const GridColumnsList = ({ columns }: { columns: NavigationColumn[] }) => (
    <Grid templateColumns="repeat(auto-fit, minmax(220px, 1fr))" sx={innerNavLeftGridStyles}>
        <GridItem colStart={1}>{filterByColumns(columns, 1)}</GridItem>
        <GridItem colStart={2}>{filterByColumns(columns, 2)}</GridItem>
        <GridItem colStart={3}>{filterByColumns(columns, 3)}</GridItem>
        <GridItem colStart={4}>{filterByColumns(columns, 4)}</GridItem>
    </Grid>
);

const DesktopNavItem = ({
    navigationItem,
    isOpen,
    heightNav,
    setHeightNav,
    onOpen: onOpenParent,
    onClose: onCloseParent,
    isHoveringNavBar,
    shouldRenderDropdown,
    onItemHover,
    onItemClose,
    onItemEnter,
    isActive = true,
}: DesktopNavItemProps) => {
    const { onOpen, onClose, isOpen: isOpenInner } = useDisclosure({ defaultIsOpen: false });
    const { get: getMicrocopy } = useMicrocopy();
    const [openButton, setOpenButtonFocus] = useFocus();
    const [closeButton, setCloseButtonFocus] = useFocus();
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navItemRef = useRef<HTMLDivElement | null>(null);
    const dropdownPanelRef = useRef<HTMLDivElement | null>(null);
    const dropdownPanelId = useId();
    const { link, menu, isSales } = navigationItem;
    const columns = menu?.columns ?? [];
    const thumbnailLinks = menu?.thumbnailLinks ?? [];
    const tiles = menu?.tiles ?? [];
    const fallbackMedia = menu?.fallbackMedia;
    const seeAllLabel = menu?.seeAllLabel;

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
            document.body.style.overflowY = "";
        };
    }, []);

    const clearOpenTimeout = () => {
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    };

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const openDropdown = useCallback(() => {
        clearCloseTimeout();
        clearOpenTimeout();

        document.body.style.overflowY = "scroll";
        onOpen();
        onOpenParent?.();
    }, [onOpen, onOpenParent]);

    const scheduleOpen = useCallback(() => {
        if (!isHoveringNavBar) return;
        onItemEnter?.();
        clearCloseTimeout();
        clearOpenTimeout();
        openTimeoutRef.current = setTimeout(() => {
            openDropdown();
            onItemHover?.();
        }, NAV_HOVER_DELAY_MS);
    }, [isHoveringNavBar, openDropdown, onItemHover, onItemEnter]);

    const scheduleClose = useCallback(
        (options?: { closeParent?: boolean }) => {
            clearOpenTimeout();
            closeTimeoutRef.current = setTimeout(() => {
                if (dropdownPanelRef.current?.contains(document.activeElement)) {
                    return;
                }

                document.body.style.overflowY = "";
                onClose();

                if (options?.closeParent === false) return;

                onCloseParent?.();
                onItemClose?.();
            }, NAV_HOVER_DELAY_MS);
        },
        [onClose, onCloseParent, onItemClose]
    );

    const handleHoverZoneEnter = useCallback(() => {
        if (!isHoveringNavBar) return;
        onItemEnter?.();
        clearCloseTimeout();
    }, [isHoveringNavBar, onItemEnter]);

    const handleLinkMouseLeave = useCallback((event: ReactMouseEvent<HTMLElement>) => {
        const next = event.relatedTarget;

        if (next instanceof Node && navItemRef.current?.contains(next)) return;

        clearOpenTimeout();
    }, []);

    const handlePrimaryLinkKeyDown = useCallback(
        (e: ReactKeyboardEvent<HTMLElement>) => {
            if (e.key !== "Tab" || e.shiftKey || e.defaultPrevented || !shouldRenderDropdown) {
                return;
            }

            e.preventDefault();
            const focusFirstInPanel = () => {
                const items = queryPanelFocusables(dropdownPanelRef.current);
                items[0]?.focus();
            };

            if (!isOpenInner) {
                openDropdown();
                requestAnimationFrame(() => {
                    requestAnimationFrame(focusFirstInPanel);
                });
            } else {
                requestAnimationFrame(focusFirstInPanel);
            }
        },
        [shouldRenderDropdown, isOpenInner, openDropdown]
    );

    const handlePanelKeyDown = useCallback(
        (e: ReactKeyboardEvent<HTMLDivElement>) => {
            if (e.key !== "Tab" || e.defaultPrevented) {
                return;
            }

            const active = document.activeElement;

            if (!active || !dropdownPanelRef.current?.contains(active)) {
                return;
            }

            const items = queryPanelFocusables(dropdownPanelRef.current);

            if (items.length === 0) {
                return;
            }

            const focusIndex = items.findIndex((el) => el === active || el.contains(active));

            if (focusIndex < 0) {
                return;
            }

            if (e.shiftKey) {
                if (focusIndex === 0) {
                    e.preventDefault();
                    getPrimaryNavTrigger(link.testId || link.label)?.focus({
                        preventScroll: true,
                    });
                }
            } else if (focusIndex === items.length - 1) {
                const list = getPrimaryNavTriggers();
                const i = list.indexOf(getPrimaryNavTrigger(link.testId || link.label));

                if (i >= 0 && i < list.length - 1) {
                    e.preventDefault();
                    list[i + 1].focus({ preventScroll: true });
                } else if (i === list.length - 1) {
                    if (focusDesktopSearchTrigger()) {
                        e.preventDefault();
                    }
                }
            }
        },
        [link]
    );

    const handleLinkMouseEnter = useCallback(() => {
        if (!shouldRenderDropdown) return;

        if (isOpen) {
            onItemEnter?.();
            clearCloseTimeout();
            clearOpenTimeout();
            openDropdown();
            onItemHover?.();
            return;
        }

        scheduleOpen();
    }, [shouldRenderDropdown, isOpen, onItemEnter, openDropdown, onItemHover, scheduleOpen]);

    const handlePlainMouseEnter = useCallback(() => {
        if (!isHoveringNavBar) return;
        onItemEnter?.();
        clearOpenTimeout();
        clearCloseTimeout();
    }, [isHoveringNavBar, onItemEnter]);

    const handleNavItemFocus = useCallback(() => {
        if (!shouldRenderDropdown) return;
        clearCloseTimeout();
        clearOpenTimeout();
        openDropdown();
        onItemHover?.();
    }, [shouldRenderDropdown, openDropdown, onItemHover]);

    const handleLeaveTo = useCallback(
        (next: EventTarget | null) => {
            const nextNode = next instanceof Node ? next : null;
            const movedToSiblingTopLevel =
                Boolean((next as Element | null)?.closest?.('[data-testid="DesktopNavItem"]')) &&
                !navItemRef.current?.contains(nextNode);

            clearOpenTimeout();
            clearCloseTimeout();
            scheduleClose({ closeParent: !movedToSiblingTopLevel });
        },
        [scheduleClose]
    );

    const handleNavItemBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget as HTMLElement | null;

            if (navItemRef.current?.contains(next) || dropdownPanelRef.current?.contains(next)) {
                return;
            }

            handleLeaveTo(next);
        },
        [handleLeaveTo]
    );

    const handleMouseLeave = useCallback(
        (event: ReactMouseEvent<HTMLDivElement>) => {
            const next = event.relatedTarget;

            if (next instanceof Node && navItemRef.current?.contains(next)) return;

            handleLeaveTo(next);
        },
        [handleLeaveTo]
    );

    const handleDropdownMouseEnter = useCallback(() => {
        document.body.style.overflowY = "scroll";
        handleHoverZoneEnter();
    }, [handleHoverZoneEnter]);

    const handleDropdownMouseLeave = useCallback(() => {
        document.body.style.overflowY = "";
        scheduleClose();
    }, [scheduleClose]);

    const closeDropdown = useCallback(() => {
        clearOpenTimeout();
        clearCloseTimeout();
        document.body.style.overflowY = "";
        onClose();
        onCloseParent();
        onItemClose?.();
    }, [onClose, onCloseParent, onItemClose]);

    const handleMenuLinkClick = useCallback(
        (e: ReactMouseEvent) => {
            const a = (e.target as Element | null)?.closest?.("a");
            if (a instanceof HTMLAnchorElement && a.pathname === window.location.pathname) {
                closeDropdown();
            }
        },
        [closeDropdown]
    );

    useEffect(() => {
        if (!isOpenInner || !shouldRenderDropdown) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape" || event.defaultPrevented) return;

            const a = document.activeElement;

            if (!navItemRef.current?.contains(a) && !dropdownPanelRef.current?.contains(a)) {
                return;
            }

            event.preventDefault();
            closeDropdown();
            getPrimaryNavTrigger(link.testId || link.label)?.focus({
                preventScroll: true,
            });
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpenInner, shouldRenderDropdown, closeDropdown, link]);

    const goToCloser = () => {
        if (isOpenInner) onClose();
        else onOpen();
        setCloseButtonFocus();
    };

    const goToOpener = () => {
        if (isOpenInner) onClose();
        else onOpen();
        setOpenButtonFocus();
    };

    const navStyles = isSales
        ? { container: salesNavItemContainerStyles, item: salesNavItemStyles }
        : { container: navItemContainerStyles, item: navItemStyles };

    useEffect(() => {
        if (!isOpen && isOpenInner) {
            onOpenParent?.();
        }
    }, [isOpen, isOpenInner, onOpenParent]);

    useEffect(() => {
        if (!isOpen) {
            setHeightNav(0);
            return;
        }
        if (isOpenInner) {
            setHeightNav(heightNav);
        }
    }, [isOpen, isOpenInner, setHeightNav, heightNav]);

    const hasFourColumns = checkFourColumns(columns);
    const categoryLabel = link.label;

    const linkContent = (
        <>
            <ContentLink
                link={link.contentLink}
                sx={navStyles.item}
                data-desktop-nav-primary="true"
                data-testid={link.testId || link.label || "navigation-item"}
                aria-haspopup={shouldRenderDropdown ? "true" : undefined}
                aria-expanded={shouldRenderDropdown ? isActive && isOpenInner : undefined}
                aria-controls={shouldRenderDropdown ? dropdownPanelId : undefined}
                onKeyDown={handlePrimaryLinkKeyDown}
                onMouseEnter={shouldRenderDropdown ? handleLinkMouseEnter : undefined}
                onMouseLeave={shouldRenderDropdown ? handleLinkMouseLeave : undefined}
            >
                {categoryLabel}
            </ContentLink>

            <VisuallyHidden>
                <Button tabIndex={-1} ref={openButton} onClick={goToCloser} data-testid="enterMenu">
                    {getMicrocopy(CommonCMS.shared, CommonCMS.navigationMenu.ariaButtonOpenMenu, {
                        replacements: { categoryName: categoryLabel },
                    })}
                </Button>
            </VisuallyHidden>
        </>
    );

    const focusBlurHandlers = {
        onFocus: handleNavItemFocus,
        onBlur: handleNavItemBlur,
    };

    const navItemRootProps = shouldRenderDropdown
        ? {
              onMouseEnter: handleHoverZoneEnter,
              sx: navItemHoverWrapperStyles,
          }
        : {
              onMouseEnter: handlePlainMouseEnter,
              align: "center" as const,
              alignSelf: "center" as const,
              sx: navStyles.container,
          };

    return (
        <>
            <Flex
                ref={navItemRef}
                data-testid="DesktopNavItem"
                onMouseLeave={handleMouseLeave}
                onClick={shouldRenderDropdown ? handleMenuLinkClick : undefined}
                {...navItemRootProps}
                {...focusBlurHandlers}
            >
                {shouldRenderDropdown ? (
                    <Flex align="center" sx={navStyles.container}>
                        {linkContent}
                    </Flex>
                ) : (
                    linkContent
                )}
            </Flex>

            {shouldRenderDropdown && isOpenInner && isActive && (
                <Portal>
                    <Box
                        ref={dropdownPanelRef}
                        {...animatedContainerStyles}
                        id={dropdownPanelId}
                        role="region"
                        data-testid={MENU_DROPDOWN}
                        aria-label={categoryLabel ? `${categoryLabel} menu` : "Submenu"}
                        onMouseEnter={handleDropdownMouseEnter}
                        onMouseLeave={handleDropdownMouseLeave}
                        onKeyDown={handlePanelKeyDown}
                        onClick={handleMenuLinkClick}
                    >
                        <VisuallyHidden>
                            <Button
                                tabIndex={-1}
                                ref={closeButton}
                                onClick={goToOpener}
                                data-testid="exitMenu"
                            >
                                {getMicrocopy(
                                    CommonCMS.shared,
                                    CommonCMS.navigationMenu.ariaButtonCloseMenu,
                                    { replacements: { categoryName: categoryLabel } }
                                )}
                            </Button>
                        </VisuallyHidden>
                        <Box {...menuContainerStyles}>
                            {/* Left section */}
                            <Box sx={outerNavLeftStyles}>
                                {thumbnailLinks.length > 0 ? (
                                    <ThumbnailGrid
                                        thumbnailLinks={thumbnailLinks}
                                        isVisible={isOpenInner}
                                    />
                                ) : (
                                    <GridColumnsList columns={columns} />
                                )}
                                {seeAllLabel && thumbnailLinks.length === 0 && (
                                    <ContentLink
                                        sx={bottomContainerLinkStyles}
                                        link={link.contentLink}
                                    >
                                        {seeAllLabel}
                                        <ECCOIcon name="arrow-right" />
                                    </ContentLink>
                                )}
                            </Box>

                            {/* Right section */}
                            <Flex
                                sx={{
                                    ...innerNavRightStyles,
                                    maxW: hasFourColumns ? "25%" : "50%",
                                }}
                            >
                                {tiles.length > 0 ? (
                                    tiles.map((tile) => (
                                        <CategoryTile
                                            key={tile.id}
                                            link={tile}
                                            imageSizes={["50vw"]}
                                            isVisible={isOpenInner}
                                        />
                                    ))
                                ) : (
                                    <FallbackImageNavigation image={fallbackMedia} />
                                )}
                            </Flex>
                        </Box>
                    </Box>
                </Portal>
            )}
        </>
    );
};

export default memo(DesktopNavItem);
